import { Delete } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { createAndAssignPendingAccountsToNotRecognizedPayment } from '../../../../../../../../lib/api';
import CustomButton from '../../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../../lib/designSystem/CircularProgress';
import {
  formatMoney,
  showAlert,
  validaRut,
} from '../../../../../../../../lib/functions/utils';

const AddPendingAccountToFNC = (props: any) => {
  const { notRecognizedPayment, setOpen, open, handleDetails, maxAmount } =
    props;
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);

  const addNewDocument = () => {
    setDocuments((prev: any) => [
      ...prev,
      {
        id: prev.length + 1,
        payTo: '',
        amount: 0,
        observation: '',
      },
    ]);
  };

  const onChangeDocument = (e: any, index: number, name: string) => {
    let { value } = e.target;
    if (name === 'amount') {
      value = value.split('$').join('').split('.').join('');
    }
    if (name === 'payTo') {
      value = value.toUpperCase();
    }
    setDocuments((prev: any) =>
      prev.map((document: any, i: number) =>
        i === index ? { ...document, [name]: value } : document,
      ),
    );
  };

  const removeDocument = (index: number) => {
    setDocuments((prev: any) =>
      prev.filter((_: any, i: number) => i !== index),
    );
  };

  const validatePayTo = () => {
    if (documents.length === 0) return false;
    const validPayTo = documents.every(
      (document: any) => document.payTo && validaRut(document.payTo),
    );
    const validAmount = documents.every(
      (document: any) => document.amount && document.amount > 0,
    );
    const validObservation = documents.every(
      (document: any) =>
        document.observation && document.observation.length > 0,
    );
    const validCategoryId = documents.every(
      (document: any) => document.categoryId && document.categoryId > 0,
    );
    return validPayTo && validAmount && validObservation && validCategoryId;
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (!validatePayTo()) {
      showAlert(
        dispatch,
        'error',
        'Debes completar el campo de "Pagar al RUT" correctamente. El rut no debe contener puntos pero debe contener guión.',
      );
      setLoading(false);
      return;
    }
    try {
      await createAndAssignPendingAccountsToNotRecognizedPayment(
        dispatch,
        notRecognizedPayment.id,
        { pendingAccounts: documents },
      );
      showAlert(
        dispatch,
        'success',
        'Se han creado las cuentas pendientes correctamente',
      );
      await handleDetails(notRecognizedPayment.id, true);
    } catch (error) {
      showAlert(
        dispatch,
        'error',
        'Ha ocurrido un error al intentar crear las cuentas pendientes',
      );
    }

    setLoading(false);
  };

  const raiseAlert = () => {
    return (
      maxAmount < documents.reduce((acc, curr) => acc + Number(curr.amount), 0)
    );
  };

  const dispatch = useDispatch();

  return (
    <Dialog maxWidth="lg" open={open} onClose={() => setOpen(false)}>
      <DialogTitle>Agregar cuentas pendientes - FNC</DialogTitle>
      {loading ? (
        <DialogContent style={{ minHeight: 100 }}>
          <CenteredCircularProgress type="dialog" />
        </DialogContent>
      ) : (
        <DialogContent style={{ minHeight: 100 }}>
          <div>
            <Typography variant="body1">
              Monto restante: {formatMoney(maxAmount)}
            </Typography>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 0px',
            }}
          >
            <Typography variant="body1" fontWeight="bold">
              Agregar cuenta pendiente
            </Typography>
            <div>
              <CustomButton onClick={addNewDocument}>Agregar</CustomButton>
            </div>
          </div>
          <div>
            {documents.map((document: any, index: number) => (
              <div key={index} style={{ padding: '8px 0px' }}>
                <Grid container spacing={2} rowSpacing={2} alignItems="center">
                  <Grid item xs={2}>
                    <TextField
                      fullWidth
                      label="Pagar al RUT"
                      value={document.payTo}
                      onChange={(e) => onChangeDocument(e, index, 'payTo')}
                      error={document.payTo && !validaRut(document.payTo)}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      fullWidth
                      label="Monto"
                      value={formatMoney(document.amount)}
                      onChange={(e) => onChangeDocument(e, index, 'amount')}
                      error={document.amount <= 0}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">
                        Tipo de cuenta
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={document.categoryId}
                        label="Tipo de cuenta"
                        error={!document.categoryId}
                        onChange={(e) =>
                          onChangeDocument(e, index, 'categoryId')
                        }
                      >
                        <MenuItem value={3}>Factura no cedida</MenuItem>
                        {/* <MenuItem value={-1}>
                          Recaudación por pagar a fondo
                        </MenuItem> */}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Observación"
                      value={document.observation}
                      onChange={(e) =>
                        onChangeDocument(e, index, 'observation')
                      }
                      error={document.observation.length === 0}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton onClick={() => removeDocument(index)}>
                      <Delete />
                    </IconButton>
                  </Grid>
                </Grid>
              </div>
            ))}
            <Divider />
            <Grid
              container
              spacing={2}
              rowSpacing={2}
              alignItems="center"
              paddingTop={2}
            >
              <Grid item xs={2}>
                <Typography variant="body1" fontWeight="bold">
                  Total
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body1" fontWeight="bold">
                  {formatMoney(
                    documents.reduce(
                      (acc, curr) => acc + Number(curr.amount),
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
          </div>
          {raiseAlert() && (
            <div style={{ color: 'red', marginTop: '12px' }}>
              <Typography variant="body1">
                El monto total de las cuentas pendientes excede el monto máximo
                permitido
              </Typography>
            </div>
          )}
        </DialogContent>
      )}
      <DialogActions>
        <CustomButton
          onClick={() => {
            setOpen(false);
          }}
          color="secondary"
        >
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={() => handleSubmit()}
          disabled={
            !documents.length || loading || raiseAlert() || !validatePayTo()
          }
        >
          Crear
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddPendingAccountToFNC;
